import React, { useMemo, useState } from "react";
import { FiEdit2 } from "react-icons/fi";
import { FaPaintBrush, FaUser } from "react-icons/fa";
import { Box, FlexBox, Image } from "../primitives";
import { LabelStyled, ScrollStyles } from "../common/common.style";
import { useAddCommas } from "../../utils";
import { FaStar } from "react-icons/fa";
import { InfoTooltip } from "../common/InfoTooltip/InfoTooltip";
import {
  CARD_CONDITIONS_CARDMARKET,
  CARD_LANGUAGES,
  CONDITION_MAPPING_CARDTRADER_CARDMARKET,
} from "../../constant";
import {
  CARDMARKET_SELLER_ACOUNTRY_CODE,
  CardMarketConditionCodeType,
  CardTraderLanguageValueType,
  ProductPriceDataCardMarketType,
  ProductPriceDataProductCardMarketType,
} from "magic-shared-types";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCountryCm,
  selectSellerType,
  setCountryCm,
  setSellerType,
} from "../../store/slices/modalPricing";
import { CardSelected } from "../../type";
import { LanguageDropdown } from "../common/LanguageDropdown/LanguageDropdown";
import { ConditionDropdownCardMarket } from "../common/ConditionDropdownCardMarket/ConditionDropdownCardMarket";
import { getMatchingConditionsCardMarket } from "../../utils/condition";
import { TableRow } from "./modalPricing.styled";
import { SellerTypeCardMarketDropDown } from "../common/SellerTypeCardMarketDropDown/SellerTypeCardMarketDropDown";
import { ReversIcon } from "../common/CardsIconExtra/ReversIcon";
import { FirstEditionIcon } from "../common/CardsIconExtra/FirstEditionIcon";
import { CheckBox } from "../common/CheckBox/CheckBox";
import { selectGame } from "../../store/slices/global";
import { CountryDropdownCardMarket } from "../common/CountryDropdown.tsx/CountryDropDownCardMarket";
import { useCards } from "../../context/CardsContext";
import dayjs from "dayjs";

interface CardTraderTableProps {
  cardMarketPrice?: ProductPriceDataCardMarketType;
  cardMarketCards?: ProductPriceDataProductCardMarketType[];
  cardSelected: CardSelected;
  setCardsSelected: (arg1: CardSelected) => void;
  editedAt?: string;
}

export const CardMarketTable = ({
  cardMarketPrice,
  cardMarketCards,
  cardSelected,
  setCardsSelected,
  editedAt,
}: CardTraderTableProps) => {
  const addCommas = useAddCommas();
  const [language, setLanguage] = useState<CardTraderLanguageValueType>(
    cardSelected.language
  );
  const [condition, setCondition] = useState<CardMarketConditionCodeType>(
    CARD_CONDITIONS_CARDMARKET.find(
      (cond) =>
        cond.value ===
        CONDITION_MAPPING_CARDTRADER_CARDMARKET[cardSelected.condition][
          CONDITION_MAPPING_CARDTRADER_CARDMARKET[cardSelected.condition]
            .length - 1
        ]
    )?.code || "NM"
  );
  const [isReverseHolo, setIsReverseHolo] = useState(cardSelected.isReversed);
  const [isFirstEdition, setIsFirstEdition] = useState(
    cardSelected.isFirstEdition
  );
  const countryCm = useSelector(selectCountryCm);
  const sellerType = useSelector(selectSellerType);
  const dispatch = useDispatch();
  const game = useSelector(selectGame);
  const hasFirstEdition = ["yugioh", "pokemon"].includes(game.card_trader_game);
  const hasReverse = ["pokemon"].includes(game.card_trader_game);
  const cardComplexArray = useCards();
  const foundCardComplexArray = cardComplexArray[cardSelected.id];

  const filteredCardMarketCards = useMemo(() => {
    let cardMarketCardsToFilter = cardMarketCards ? [...cardMarketCards] : [];

    if (language) {
      const foundLanguage = CARD_LANGUAGES.find(
        (lang) => lang.value === language
      );
      cardMarketCardsToFilter = cardMarketCardsToFilter.filter(
        (product) =>
          foundLanguage?.label?.toLowerCase() ===
          product?.language?.toLowerCase()
      );
    }

    if (condition) {
      cardMarketCardsToFilter = cardMarketCardsToFilter.filter((product) =>
        getMatchingConditionsCardMarket(condition).includes(
          product.condition as CardMarketConditionCodeType
        )
      );
    }

    if (countryCm) {
      cardMarketCardsToFilter = cardMarketCardsToFilter.filter(
        (product) => countryCm?.toLowerCase() === product.location
      );
    }

    if (sellerType) {
      cardMarketCardsToFilter = cardMarketCardsToFilter.filter((product) => {
        if (sellerType === "professional") {
          return product.isProfessional === true;
        } else if (sellerType === "powerseller") {
          return product.isPowerSeller === true;
        }
        return true;
      });
    }

    cardMarketCardsToFilter = cardMarketCardsToFilter.filter(
      (product) =>
        product.isReverseHolo === isReverseHolo ||
        (!product.isReverseHolo && !isReverseHolo)
    );

    cardMarketCardsToFilter = cardMarketCardsToFilter.filter(
      (product) =>
        product.isFirstEdition === isFirstEdition ||
        (!product.isFirstEdition && !isFirstEdition)
    );

    return cardMarketCardsToFilter;
  }, [
    cardMarketCards,
    condition,
    countryCm,
    language,
    sellerType,
    isReverseHolo,
    isFirstEdition,
  ]);

  return (
    <>
      <FlexBox css={{ gap: 10, marginTop: 10 }}>
        {foundCardComplexArray.card_market_ids[0] && (
          <a
            href={`https://www.cardmarket.com/en/${game.card_market_game}/Products?idProduct=${foundCardComplexArray.card_market_ids[0]}`}
            target="_blank"
            rel="noreferrer"
          >
            View on cardmarket
          </a>
        )}
        {editedAt && <Box>{dayjs(editedAt).format("hh:mm DD-MM-YYYY")}</Box>}
      </FlexBox>
      <FlexBox css={{ gap: 10, marginTop: 20 }}>
        {cardMarketPrice?.avg_1 && (
          <Box>
            1-day Avg: <strong>{addCommas(cardMarketPrice.avg_1)} €</strong>
          </Box>
        )}
        {cardMarketPrice?.avg_7 && (
          <Box>
            7-day Avg: <strong>{addCommas(cardMarketPrice.avg_7)} €</strong>
          </Box>
        )}
        {cardMarketPrice?.avg_30 && (
          <Box>
            30-day Avg: <strong>{addCommas(cardMarketPrice.avg_30)} €</strong>
          </Box>
        )}
        {cardMarketPrice?.trend && (
          <Box>
            Trend: <strong>{addCommas(cardMarketPrice.trend)} €</strong>
          </Box>
        )}
      </FlexBox>
      <FlexBox css={{ gap: 10, marginTop: 20 }}>
        {cardMarketPrice?.avg_7_foil && (
          <Box>
            7-day Avg Foil:{" "}
            <strong>{addCommas(cardMarketPrice.avg_7_foil)} €</strong>
          </Box>
        )}
        {cardMarketPrice?.avg_30_foil && (
          <Box>
            30-day Avg Foil:{" "}
            <strong>{addCommas(cardMarketPrice.avg_30_foil)} €</strong>
          </Box>
        )}
        {cardMarketPrice?.trend_foil ? (
          <Box>
            Trend Foil:{" "}
            <strong>{addCommas(cardMarketPrice.trend_foil)} €</strong>
          </Box>
        ) : null}
      </FlexBox>
      {cardMarketCards?.length ? (
        <>
          <FlexBox css={{ marginTop: 20, gap: 20 }}>
            <LanguageDropdown
              language={language}
              setLanguageValue={(lang) => setLanguage(lang)}
            />
            <CountryDropdownCardMarket
              country={countryCm}
              setCountry={(aCountry) => {
                dispatch(setCountryCm(aCountry));
              }}
            />
            <ConditionDropdownCardMarket
              condition={condition}
              setCondition={(aCondition) => setCondition(aCondition)}
            />
            <SellerTypeCardMarketDropDown
              sellerType={sellerType}
              setSellerType={(aSelectSellerType) =>
                dispatch(setSellerType(aSelectSellerType))
              }
            />
            {hasReverse && (
              <FlexBox css={{ flexDirection: "column", gap: 10 }}>
                <LabelStyled>Reverse Holo</LabelStyled>
                <CheckBox
                  checked={isReverseHolo}
                  setChecked={(checked: boolean) => {
                    setIsReverseHolo(checked);
                  }}
                />
              </FlexBox>
            )}
            {hasFirstEdition && (
              <FlexBox css={{ flexDirection: "column", gap: 10 }}>
                <LabelStyled>First Edition</LabelStyled>
                <CheckBox
                  checked={isFirstEdition}
                  setChecked={(checked: boolean) => {
                    setIsFirstEdition(checked);
                  }}
                />
              </FlexBox>
            )}
          </FlexBox>
          <Box css={{ overflow: "auto", marginTop: 20, ...ScrollStyles }}>
            <Box css={{ height: 350 }}>
              {filteredCardMarketCards?.map((product, index) => {
                const foundCondition = CARD_CONDITIONS_CARDMARKET?.find(
                  (cond) => cond.code === product.condition
                );

                const cardLanguage = CARD_LANGUAGES?.find(
                  (lang) => lang?.label === product?.language
                );

                const location = CARDMARKET_SELLER_ACOUNTRY_CODE.find(
                  (country) => country.name.toLowerCase() === product.location
                );

                return (
                  <TableRow
                    key={`${product.sellerName}-${index}`}
                    isEven={!!(index % 2)}
                    onClick={() => {
                      setCardsSelected({
                        ...cardSelected,
                        price: product?.cardPrice,
                      });
                    }}
                  >
                    <Box css={{ width: "50%" }}>
                      <FlexBox css={{ gap: 5, alignItems: "center" }}>
                        <Box css={{ fontWeight: "bolder" }}>
                          {product?.sellerName}
                        </Box>
                        {location && (
                          <Image
                            src={location.flag}
                            alt={location?.name}
                            css={{ width: 20, height: 20 }}
                          />
                        )}
                        {product?.isPowerSeller && (
                          <InfoTooltip content="powerseller">
                            <FaUser size="20" color="grey" />
                          </InfoTooltip>
                        )}
                        {product?.isProfessional && (
                          <InfoTooltip content="professional">
                            <FaUser size="20" color="blue" />
                          </InfoTooltip>
                        )}
                      </FlexBox>
                      {product?.feedback && (
                        <FlexBox
                          css={{
                            alignItems: "center",
                            gap: 2,
                            fontSize: 14,
                            marginTop: 5,
                          }}
                        >
                          <FaStar size={12} color="grey" />
                          {addCommas(product?.feedback)}
                        </FlexBox>
                      )}
                    </Box>
                    <FlexBox
                      css={{ gap: 5, width: "30%", justifyContent: "start" }}
                    >
                      {foundCondition && (
                        <Box
                          css={{
                            padding: 5,
                            borderRadius: 5,
                            background: foundCondition?.color,
                          }}
                        >
                          {foundCondition?.code}
                        </Box>
                      )}
                      {cardLanguage && (
                        <Image
                          src={`${cardLanguage?.flag}`}
                          alt={cardLanguage?.label}
                          css={{ width: 30 }}
                        />
                      )}
                      {product?.isReverseHolo && (
                        <InfoTooltip content="Reverse Holo">
                          <Box>
                            <ReversIcon />
                          </Box>
                        </InfoTooltip>
                      )}
                      {product?.isFirstEdition && (
                        <InfoTooltip content="First Edition">
                          <Box>
                            <FirstEditionIcon />
                          </Box>
                        </InfoTooltip>
                      )}
                      {product?.isFoilCard && (
                        <InfoTooltip content="Foil">
                          <FaStar size={24} color="#FFD700" />
                        </InfoTooltip>
                      )}
                      {product?.isSignedCard && (
                        <InfoTooltip content="Signed">
                          <FiEdit2 size={24} />
                        </InfoTooltip>
                      )}
                      {product?.isAlteredCard && (
                        <InfoTooltip content="Altered">
                          <FaPaintBrush size={24} color="brown" />
                        </InfoTooltip>
                      )}
                    </FlexBox>
                    <FlexBox
                      css={{ gap: 5, width: "30%", justifyContent: "end" }}
                    >
                      <Box>{product?.quantity}x</Box>
                      {product?.cardPrice && (
                        <Box>{addCommas(product?.cardPrice)} €</Box>
                      )}
                    </FlexBox>
                  </TableRow>
                );
              })}
            </Box>
          </Box>
        </>
      ) : (
        <Box>no product found</Box>
      )}
    </>
  );
};
